import { useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';

import { TableCell, TableRow } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import {
  FilterListEmptyStateMui as FilterListEmptyState,
  FilterListEmptyStateMuiProps as FilterListEmptyStateProps,
  type Sorting,
  useDelayedLoading,
  useFilters,
} from '@spotify/confidence-core-react';
import {
  CollectionsV1ResourceType,
  WorkflowInstanceListItemFragment,
  getError,
  getTypeOrNull,
  useListWorkflowInstancesQuery,
} from '@spotify/confidence-graphql';
import { useFilterWithFavorites } from '@spotify/plugin-confidence-collections-react';

import { ROW_HEIGHT } from '../../components/WorkflowInstanceTableRow';
import { getWorkflowName } from '../../helpers/workflowPageUtils';

export type WorkflowInstanceListItem = WorkflowInstanceListItemFragment;

export type IntermediateRenderComponentProps = {
  errorText?: string;
  emptyStateProps?: Partial<FilterListEmptyStateProps>;
  showLoading?: boolean;
};

const EMPTY_STATE_COMPONENT = ({
  emptyStateProps,
  showLoading,
}: IntermediateRenderComponentProps) => (
  <FilterListEmptyState
    loading={showLoading}
    type="experiment"
    description="Create an experiment and it will show up in this list."
    {...emptyStateProps}
  />
);

const getIntermediateRenderComponent = (
  error: string | null,
  workflowInstances: WorkflowInstanceListItem[],
  isFirstLoad: boolean,
) => {
  if (error) {
    const ErrorComponent = ({
      errorText,
    }: IntermediateRenderComponentProps) => (
      <FilterListEmptyState error={errorText || error} type="experiment" />
    );
    ErrorComponent.displayName = 'ErrorComponent';
    return ErrorComponent;
  }

  if (workflowInstances.length === 0 && !isFirstLoad) {
    return EMPTY_STATE_COMPONENT;
  }

  return null;
};

type InfiniteScrollTableProps = {
  placeholderColumns?: number;
  placeholderRows?: number;
};

type UseListWorkflowInstances = {
  nextPageToken: string | null | undefined;
  loading: boolean;
  showLoading: boolean;
  fetchMore: ReturnType<typeof useListWorkflowInstancesQuery>['fetchMore'];
  error: string | undefined;
  isDefaultFilterState: boolean;
  workflowInstances: WorkflowInstanceListItem[];
  IntermediateRenderComponent:
    | ((props: IntermediateRenderComponentProps) => JSX.Element)
    | null;
  InfiniteScroll: () => JSX.Element | null;
  InfiniteScrollTableRow: (
    props?: InfiniteScrollTableProps,
  ) => JSX.Element | null;
};

export const useListWorkflowInstances = ({
  pageSize = 25,
  workflowId,
  filter: inputFilter,
  sorting,
  skip: skipQuery = false,
}: {
  pageSize?: number;
  workflowId?: string;
  filter?: string;
  sorting?: Sorting;
  skip?: boolean;
}): UseListWorkflowInstances => {
  const { isDefaultFilterState } = useFilters();
  const {
    filter: combinedFilter,
    nameFiltersLoading,
    resolvedNameFilterEmpty,
  } = useFilterWithFavorites({
    resourceType: CollectionsV1ResourceType.WorkflowInstance,
  });

  const sortingString =
    sorting?.orderBy && sorting?.order !== 'none'
      ? `${sorting.orderBy}:${sorting.order}`
      : 'createTime:desc';

  const filter = [inputFilter, combinedFilter].filter(Boolean).join(' AND ');
  const listWorkflowInstancesQueryVariables = useMemo(
    () => ({
      pageSize,
      workflow: workflowId ? getWorkflowName(workflowId) : undefined,
      filter,
      orderBy: sortingString,
    }),
    [pageSize, workflowId, filter, sortingString],
  );

  const queryResult = useListWorkflowInstancesQuery({
    variables: listWorkflowInstancesQueryVariables,
    errorPolicy: 'all',
    skip: skipQuery || nameFiltersLoading || resolvedNameFilterEmpty,
  });

  const { data, loading, error, fetchMore } = queryResult;

  const errorMessage = getError(data?.workflowInstances)?.message;
  const instancesResponse = getTypeOrNull(data?.workflowInstances);

  const [workflowInstances, setWorkflowInstances] = useState<
    WorkflowInstanceListItem[]
  >([]);

  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (instancesResponse?.workflowInstances) {
      setWorkflowInstances(instancesResponse.workflowInstances);
      setIsFirstLoad(false);
    }
  }, [instancesResponse]);

  const handleInViewChange = async (inView: boolean) => {
    const nextPageToken = instancesResponse?.nextPageToken;
    if (inView && nextPageToken) {
      await fetchMore({
        variables: {
          pageToken: nextPageToken,
        },
      });
    }
  };

  const InfiniteScroll = () =>
    instancesResponse?.nextPageToken ? (
      <InView onChange={handleInViewChange}  data-track-source-anchor="244c0ddce0;infinite-scroll-in-view"/>
    ) : null;

  const InfiniteScrollTableRow = ({
    placeholderColumns = 5,
    placeholderRows = 3,
  }: InfiniteScrollTableProps = {}) =>
    instancesResponse?.nextPageToken ? (
      <InView onChange={handleInViewChange} data-track-source-anchor="396c0c1e7a;infinite-scroll-table-row-in-view">
        {({ ref }) =>
          [...Array(placeholderRows)].map((_row, rowIndex) => (
            <TableRow
              key={rowIndex}
              ref={rowIndex === 0 ? ref : undefined}
              style={{ height: ROW_HEIGHT }}
            >
              {[...Array(placeholderColumns)].map((_, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton variant="text" width="100%" />
                </TableCell>
              ))}
            </TableRow>
          ))
        }
      </InView>
    ) : null;

  const { showLoading } = useDelayedLoading(loading);
  const IntermediateRenderComponent = resolvedNameFilterEmpty
    ? EMPTY_STATE_COMPONENT
    : getIntermediateRenderComponent(
        errorMessage ?? error?.message ?? null,
        workflowInstances,
        isFirstLoad,
      );

  return {
    nextPageToken: instancesResponse?.nextPageToken,
    loading,
    showLoading,
    fetchMore,
    error: errorMessage ?? error?.message ?? undefined,

    isDefaultFilterState,
    workflowInstances,

    IntermediateRenderComponent,
    InfiniteScroll,
    InfiniteScrollTableRow,
  };
};
